import { can } from '@clim/client-core';
import {
  DOCUMENT_TYPE,
  PHOTO_CATEGORY,
  formatBytes,
  type DossierFileRecord,
  type DossierRecord,
  photoOriginOf,
} from '@clim/domain';
import { useEffect, useState } from 'react';
import { errorMessage } from '../lib/errors';
import { isAllowedMime, preparePhoto, readDocument } from '../lib/images';
import { useEngine, useReady } from '../lib/session';
import { useOnline } from '../lib/use-online';
import { Empty, ErrorBox, Field, Input, Modal, Select } from './ui';

/** Short-lived signed URLs, reused while valid (the server issues them after a rights check). */
const urlCache = new Map<string, { url: string; until: number }>();

function useFileSrc(file: DossierFileRecord): string | null {
  const engine = useEngine();
  const { api } = useReady();
  const online = useOnline();
  const [src, setSrc] = useState<string | null>(null);
  const local = engine.uploadProgress(file.id) !== null;
  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    (async () => {
      if (local) {
        const bytes = await engine.localContent(file.id);
        if (bytes && !cancelled) {
          revoke = URL.createObjectURL(new Blob([bytes as BlobPart], { type: file.mime }));
          setSrc(revoke);
        }
        return;
      }
      if (file.status !== 'ready' || !online) return setSrc(null);
      const cached = urlCache.get(file.id);
      if (cached && cached.until > Date.now()) return setSrc(cached.url);
      const url = await api.fileUrl(file.id);
      urlCache.set(file.id, { url, until: Date.now() + 4 * 60_000 });
      if (!cancelled) setSrc(url);
    })().catch(() => setSrc(null));
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [api, engine, file.id, file.mime, file.status, local, online]);
  return src;
}

function UploadState({ file }: { file: DossierFileRecord }) {
  const engine = useEngine();
  const p = engine.uploadProgress(file.id);
  if (p) {
    const pct = p.total ? Math.round((p.sent / p.total) * 100) : 0;
    return <span className="sync-tag">{pct > 0 ? `Envoi ${pct} %` : 'À envoyer'}</span>;
  }
  if (file.status === 'pending') return <span className="sync-tag">Envoi en cours (autre appareil)</span>;
  return null;
}

function PhotoThumb({
  file,
  label,
  by,
  onOpen,
}: {
  file: DossierFileRecord;
  label: string;
  by?: string;
  onOpen(src: string | null): void;
}) {
  const src = useFileSrc(file);
  return (
    <figure className="thumb">
      <button
        type="button"
        className="thumb-img"
        onClick={() => onOpen(src)}
        aria-label={`Voir la photo ${label}`}
      >
        {src ? <img src={src} alt={label} loading="lazy" /> : <span aria-hidden>📷</span>}
      </button>
      <figcaption>
        {label}
        {by && <span className="muted small"> · {by}</span>}
        <UploadState file={file} />
      </figcaption>
    </figure>
  );
}

/** Photos techniques et documents du dossier (CDC §4.7, §4.8, §4.10) — ajout possible hors ligne. */
export function DossierFiles({ dossier }: { dossier: DossierRecord }) {
  const engine = useEngine();
  const { me, api } = useReady();
  const online = useOnline();
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [addingDoc, setAddingDoc] = useState(false);
  const [viewing, setViewing] = useState<{ file: DossierFileRecord; src: string | null } | null>(null);
  if (!can(me, 'document.read')) return null;

  const files = engine
    .list('dossier_file')
    .filter((f) => f.dossierId === dossier.id)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const photos = files.filter((f) => f.kind === 'photo');
  // photos commerciales (visite, vente) / photos du poseur (chantier)
  const interventions = engine.list('intervention').filter((i) => i.dossierId === dossier.id);
  const commercialIds = dossier.assignments.filter((a) => a.role === 'commercial').map((a) => a.userId);
  const installPhotos = photos.filter((f) => photoOriginOf(f, interventions, commercialIds) === 'install');
  const salePhotos = photos.filter((f) => !installPhotos.includes(f));
  const videos = files.filter((f) => f.kind === 'video');
  const documents = files.filter((f) => f.kind === 'document');
  const photoCats = engine
    .list('reason')
    .filter((r) => r.category === PHOTO_CATEGORY && r.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const type = engine.get('dossier_type', dossier.dossierTypeId);
  const required = Array.isArray(type?.settings.requiredPhotoCategories)
    ? (type!.settings.requiredPhotoCategories as string[])
    : [];
  const present = new Set(
    photos.map((p) => (p.categoryId ? engine.get('reason', p.categoryId)?.code : null)),
  );
  const missing = required
    .filter((c) => !present.has(c))
    .map((c) => photoCats.find((r) => r.code === c)?.label ?? c);
  const canAdd =
    can(me, 'document.upload') &&
    dossier.status !== 'cancelled' &&
    (dossier.status !== 'completed' || can(me, 'dossier.control'));
  const canRemove = (f: DossierFileRecord) =>
    can(me, 'dossier.control') ||
    (f.createdBy === me.user.id &&
      ['draft', 'to_correct', 'submitted', 'in_progress'].includes(dossier.status));
  const catLabel = (f: DossierFileRecord) =>
    (f.categoryId ? engine.get('reason', f.categoryId)?.label : null) ??
    (f.kind === 'photo' ? 'Photo' : f.kind === 'video' ? 'Vidéo' : 'Document');

  const addPhotos = async (list: FileList | null) => {
    setError(null);
    for (const file of [...(list ?? [])]) {
      try {
        const p = await preparePhoto(file);
        if (!isAllowedMime(p.mime) || !p.mime.startsWith('image/'))
          throw new Error(`« ${file.name} » : format non accepté (JPEG, PNG, WebP, HEIC)`);
        await engine.addFile(dossier.id, {
          kind: 'photo',
          categoryId: category || null,
          mime: p.mime,
          data: p.data,
          originalFilename: p.name,
        });
      } catch (e) {
        setError(errorMessage(e));
      }
    }
  };
  const addVideos = async (list: FileList | null) => {
    setError(null);
    for (const file of [...(list ?? [])]) {
      try {
        const mime =
          file.type === 'video/quicktime' || /\.mov$/i.test(file.name)
            ? 'video/quicktime'
            : file.type === 'video/mp4' || /\.mp4$/i.test(file.name)
              ? 'video/mp4'
              : null;
        if (!mime) throw new Error(`« ${file.name} » : format non accepté (vidéo MP4 ou MOV)`);
        const data = new Uint8Array(await file.arrayBuffer());
        await engine.addFile(dossier.id, {
          kind: 'video',
          categoryId: category || null,
          mime,
          data,
          originalFilename: file.name,
        });
      } catch (e) {
        setError(errorMessage(e));
      }
    }
  };
  /** vidéo : lue en flux depuis le cloud au moment où on l'ouvre (rien n'est téléchargé d'avance) */
  const playVideo = async (f: DossierFileRecord) => {
    setError(null);
    try {
      const local = await engine.localContent(f.id);
      setViewing({
        file: f,
        src: local
          ? URL.createObjectURL(new Blob([local as BlobPart], { type: f.mime }))
          : online
            ? await api.fileUrl(f.id)
            : null,
      });
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const open = async (f: DossierFileRecord) => {
    try {
      const local = await engine.localContent(f.id);
      const url = local
        ? URL.createObjectURL(new Blob([local as BlobPart], { type: f.mime }))
        : await api.fileUrl(f.id);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const remove = (f: DossierFileRecord) => {
    if (confirm(`Supprimer « ${f.name || catLabel(f)} » ?`))
      engine.removeFile(f.id).catch((e) => setError(errorMessage(e)));
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Photos</h2>
      </div>
      {missing.length > 0 && <p className="field-error">Photos demandées : {missing.join(', ')}</p>}
      {photos.length === 0 ? (
        <Empty>Aucune photo.</Empty>
      ) : (
        (
          [
            ['sale', '🧾 Photos commerciales (visite, vente)', salePhotos],
            ['install', '🔧 Photos du poseur (chantier)', installPhotos],
          ] as const
        ).map(([key, title, list]) => (
          <section key={key} className={`photo-group photo-${key}`} data-testid={`photos-${key}`}>
            <h3>
              {title} <span className="muted">({list.length})</span>
            </h3>
            {list.length === 0 ? (
              <Empty>{key === 'install' ? 'Pas encore de photo du poseur.' : 'Aucune photo commerciale.'}</Empty>
            ) : (
              <div className="thumbs">
                {list.map((f) => (
                  <PhotoThumb
                    key={f.id}
                    file={f}
                    label={catLabel(f)}
                    by={f.createdByName}
                    onOpen={(src) => setViewing({ file: f, src })}
                  />
                ))}
              </div>
            )}
          </section>
        ))
      )}
      {canAdd && (
        <div className="form inline-form">
          <Field label="Catégorie de la photo">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">—</option>
              {photoCats.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </Select>
          </Field>
          {/* « capture » ouvre directement l'appareil photo : un 2e bouton sans « capture » ouvre la galerie */}
          <label className="btn primary file-btn">
            📷 Prendre une photo
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              aria-label="Prendre une photo"
              onChange={(e) => void addPhotos(e.target.files).then(() => (e.target.value = ''))}
            />
          </label>
          <label className="btn file-btn">
            🖼️ Photos de la galerie
            <input
              type="file"
              accept="image/*"
              multiple
              aria-label="Ajouter des photos de la galerie"
              onChange={(e) => void addPhotos(e.target.files).then(() => (e.target.value = ''))}
            />
          </label>
          <label className="btn file-btn">
            🎬 Filmer une vidéo
            <input
              type="file"
              accept="video/mp4,video/quicktime"
              capture="environment"
              aria-label="Filmer une vidéo"
              onChange={(e) => void addVideos(e.target.files).then(() => (e.target.value = ''))}
            />
          </label>
          <label className="btn file-btn">
            🎞️ Vidéo de la galerie
            <input
              type="file"
              accept="video/*"
              multiple
              aria-label="Ajouter une vidéo de la galerie"
              onChange={(e) => void addVideos(e.target.files).then(() => (e.target.value = ''))}
            />
          </label>
        </div>
      )}

      <div className="card-head">
        <h2>Vidéos</h2>
      </div>
      {videos.length === 0 ? (
        <Empty>Aucune vidéo.</Empty>
      ) : (
        <ul className="plain files" aria-label="Vidéos">
          {videos.map((f) => (
            <li key={f.id}>
              <div className="row-main">
                <strong>🎬 {catLabel(f)}</strong>
                <span className="muted">
                  {formatBytes(f.sizeBytes)} · {f.createdByName}
                </span>
              </div>
              <UploadState file={f} />
              <button
                className="btn small"
                onClick={() => void playVideo(f)}
                disabled={f.status !== 'ready' && engine.uploadProgress(f.id) === null}
              >
                Lire
              </button>
              {canRemove(f) && (
                <button
                  className="btn small ghost"
                  aria-label={`Supprimer la vidéo ${catLabel(f)}`}
                  onClick={() => remove(f)}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="card-head">
        <h2>Documents</h2>
        {canAdd && (
          <button className="btn small primary" onClick={() => setAddingDoc(true)}>
            + Ajouter un document
          </button>
        )}
      </div>
      {documents.length === 0 ? (
        <Empty>Aucun document.</Empty>
      ) : (
        <ul className="plain files">
          {documents.map((f) => (
            <li key={f.id}>
              <div className="row-main">
                <strong>{f.name}</strong>
                <span className="muted">
                  {catLabel(f)} · {formatBytes(f.sizeBytes)} · {f.createdByName}
                </span>
              </div>
              <UploadState file={f} />
              <button
                className="btn small"
                onClick={() => void open(f)}
                disabled={f.status !== 'ready' && engine.uploadProgress(f.id) === null}
              >
                Ouvrir
              </button>
              {canRemove(f) && (
                <button
                  className="btn small ghost"
                  aria-label={`Supprimer ${f.name}`}
                  onClick={() => remove(f)}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!online && files.some((f) => f.status === 'ready' && engine.uploadProgress(f.id) === null) && (
        <p className="muted">Hors ligne : les fichiers déjà envoyés s’afficheront au retour du réseau.</p>
      )}
      <ErrorBox message={error} />

      {addingDoc && <AddDocument dossierId={dossier.id} onClose={() => setAddingDoc(false)} />}
      {viewing && (
        <Modal title={catLabel(viewing.file)} onClose={() => setViewing(null)}>
          {!viewing.src ? (
            <Empty>Disponible en ligne.</Empty>
          ) : viewing.file.kind === 'video' ? (
            <video
              className="photo-full"
              src={viewing.src}
              controls
              autoPlay
              playsInline
              preload="metadata"
              aria-label={`Vidéo ${catLabel(viewing.file)}`}
            />
          ) : (
            <img className="photo-full" src={viewing.src} alt={catLabel(viewing.file)} />
          )}
          <p className="muted">
            {viewing.file.createdByName} · {formatBytes(viewing.file.sizeBytes)}
          </p>
          <div className="actions">
            {canRemove(viewing.file) && (
              <button
                className="btn danger-outline"
                onClick={() => {
                  remove(viewing.file);
                  setViewing(null);
                }}
              >
                Supprimer
              </button>
            )}
            <button className="btn" onClick={() => setViewing(null)}>
              Fermer
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AddDocument({ dossierId, onClose }: { dossierId: string; onClose(): void }) {
  const engine = useEngine();
  const [name, setName] = useState('');
  const [typeId, setTypeId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const types = engine
    .list('reason')
    .filter((r) => r.category === DOCUMENT_TYPE && r.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const save = async () => {
    setError(null);
    try {
      if (!file) throw new Error('Choisissez un fichier');
      const doc = await readDocument(file);
      if (!isAllowedMime(doc.mime)) throw new Error('Format non accepté : PDF ou photo');
      await engine.addFile(dossierId, {
        kind: 'document',
        name,
        categoryId: typeId || null,
        mime: doc.mime,
        data: doc.data,
        originalFilename: file.name,
      });
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Modal title="Ajouter un document" onClose={onClose}>
      <div className="form">
        <Field label="Type de document">
          <Select
            value={typeId}
            onChange={(e) => {
              setTypeId(e.target.value);
              const t = types.find((x) => x.id === e.target.value);
              if (t && !name) setName(t.label);
            }}
          >
            <option value="">—</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Nom du document">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. KBIS société" />
        </Field>
        <Field label="Fichier (PDF ou photo)">
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn primary" disabled={!name.trim() || !file} onClick={() => void save()}>
            Ajouter
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Photos de la pose d'un chantier, ouvertes directement depuis le planning (fenêtre de l'intervention) : photos prises
 * par le(s) poseur(s), depuis le cloud.
 */
export function InstallPhotosDialog({ dossierId, onClose }: { dossierId: string; onClose(): void }) {
  const engine = useEngine();
  const [viewing, setViewing] = useState<{ file: DossierFileRecord; src: string | null } | null>(null);
  const dossier = engine.get('dossier', dossierId);
  const interventions = engine.list('intervention').filter((i) => i.dossierId === dossierId);
  const commercialIds = (dossier?.assignments ?? []).filter((a) => a.role === 'commercial').map((a) => a.userId);
  const photos = engine
    .list('dossier_file')
    .filter((f) => f.dossierId === dossierId && f.kind === 'photo' && photoOriginOf(f, interventions, commercialIds) === 'install')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const label = (f: DossierFileRecord) => (f.categoryId ? engine.get('reason', f.categoryId)?.label : null) ?? 'Photo';
  return (
    <Modal title={`Photos de la pose${dossier?.reference ? ` — ${dossier.reference}` : ''}`} onClose={onClose}>
      {viewing ? (
        <>
          {viewing.src ? <img className="photo-full" src={viewing.src} alt={label(viewing.file)} /> : <Empty>Disponible en ligne.</Empty>}
          <p className="muted">
            {label(viewing.file)} · {viewing.file.createdByName}
          </p>
          <div className="actions">
            <button className="btn" onClick={() => setViewing(null)}>
              ← Toutes les photos
            </button>
          </div>
        </>
      ) : photos.length ? (
        <div className="thumbs" data-testid="install-photos">
          {photos.map((f) => (
            <PhotoThumb key={f.id} file={f} label={label(f)} by={f.createdByName} onOpen={(src) => setViewing({ file: f, src })} />
          ))}
        </div>
      ) : (
        <Empty>Le poseur n’a pas encore ajouté de photo.</Empty>
      )}
    </Modal>
  );
}
